import type { AuthProviderApi, AuthState, AuthUser } from './types';
import { oktaAuth } from './oktaConfig';

let state: AuthState = { status: 'loading' };
const listeners = new Set<(s: AuthState) => void>();

function set(next: AuthState) {
  state = next;
  listeners.forEach((fn) => fn(state));
}

let tokenHooksAttached = false;

function attachTokenHooks() {
  if (tokenHooksAttached) return;
  tokenHooksAttached = true;
  // Let Okta AutoRenewService handle `expired` (renew via refresh token or silent SSO).
  // Only clear the session when renew fails.
  oktaAuth.tokenManager.on('renewed', () => {
    void refreshFromApi();
  });
  oktaAuth.tokenManager.on('error', () => {
    markUnauthenticated();
  });
}

async function loadMe(): Promise<AuthState> {
  const token = await oktaAuth.getOrRenewAccessToken();
  if (!token) return { status: 'unauthenticated' };
  const res = await fetch('/api/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) return { status: 'unauthenticated' };
  if (!res.ok) return { status: 'unauthenticated' };
  const data = (await res.json()) as {
    authenticated?: boolean;
    id?: number;
    username?: string;
    role?: string;
  };
  if (!data.authenticated || data.username == null || data.id == null) {
    return { status: 'unauthenticated' };
  }
  const role = data.role === 'admin' ? 'admin' : 'standard';
  const user: AuthUser = {
    id: String(data.id),
    username: data.username,
    role,
  };
  return { status: 'authenticated', user };
}

async function refreshFromApi() {
  const next = await loadMe();
  set(next);
}

export async function getAccessToken(): Promise<string | undefined> {
  const t = await oktaAuth.getOrRenewAccessToken();
  return t ?? undefined;
}

/** Force a token renew (refresh token or silent SSO), even if the current access token is not expired yet. */
export async function forceRenewAccessToken(): Promise<string | undefined> {
  try {
    const key = oktaAuth.tokenManager.getStorageKeyByType('accessToken') ?? 'accessToken';
    const token = await oktaAuth.tokenManager.renew(key);
    return token && 'accessToken' in token ? String(token.accessToken) : undefined;
  } catch {
    return undefined;
  }
}

export function markUnauthenticated() {
  oktaAuth.tokenManager.clear();
  set({ status: 'unauthenticated' });
}

export const oktaAuthProvider: AuthProviderApi = {
  getState: () => state,

  subscribe(fn) {
    listeners.add(fn);
    fn(state);
    return () => listeners.delete(fn);
  },

  async init() {
    set({ status: 'loading' });
    attachTokenHooks();
    try {
      await oktaAuth.start();
      const next = await loadMe();
      set(next);
    } catch {
      set({ status: 'unauthenticated' });
    }
  },

  async login() {
    throw new Error('Use Okta sign-in');
  },

  async logout() {
    await oktaAuth.signOut();
    set({ status: 'unauthenticated' });
  },

  markUnauthenticated,
};
